import { useEffect, useState } from 'react';
import { isQuietOrSnoozed } from '../lib/notificationAttentionPolicy';
import type { NotificationPreferenceConfig } from '../types/notification';

export interface NotificationQuietState {
  /** Quiet hours or snooze are active right now. */
  active: boolean;
  /** `hideBadgeWhileQuiet` is on AND attention is currently muted. */
  hidden: boolean;
}

/** Re-evaluate the boundary at most once a minute while muted. */
const MAX_RECHECK_MS = 60_000;
/** Never schedule a timer further out than this (setTimeout's 32-bit limit). */
const MAX_TIMEOUT_MS = 2_147_483_000;

const INACTIVE: NotificationQuietState = { active: false, hidden: false };

function millisecondsUntilNextMinute(now: Date): number {
  return 60_000 - (now.getSeconds() * 1000 + now.getMilliseconds());
}

function snoozeRemainingMs(config: NotificationPreferenceConfig, now: Date): number | null {
  if (config.snoozedUntil == null) return null;
  const until = new Date(config.snoozedUntil).getTime();
  if (Number.isNaN(until)) return null;
  const remaining = until - now.getTime();
  return remaining > 0 ? remaining : null;
}

/**
 * Whether foreground attention is currently muted by quiet hours or snooze, for
 * the given preference config, re-evaluated at the moment the window changes.
 *
 * The initial value is computed synchronously so a component never renders one
 * frame of the wrong state, then a boundary timer (cleaned up on unmount and on
 * every config change) flips it exactly when the quiet window ends or the snooze
 * expires — a quiet window that closes while the tab stays open restores the
 * badge without a reload.
 */
export function useNotificationQuiet(
  config: NotificationPreferenceConfig | null | undefined,
): NotificationQuietState {
  const [state, setState] = useState<NotificationQuietState>(INACTIVE);

  useEffect(() => {
    if (!config) {
      setState(INACTIVE);
      return;
    }

    let timer: number | undefined;

    const evaluate = () => {
      const now = new Date();
      const { quiet, snooze } = isQuietOrSnoozed(config, now);
      const active = quiet || snooze;
      setState({ active, hidden: active && config.hideBadgeWhileQuiet === true });
      if (timer) {
        window.clearTimeout(timer);
        timer = undefined;
      }
      if (!active) return;
      // Snooze has an exact expiry; quiet hours are wall-clock, so re-check on
      // the next minute boundary and let that tick resolve the window.
      const snoozeMs = snooze ? snoozeRemainingMs(config, now) : null;
      const delay = Math.min(snoozeMs ?? millisecondsUntilNextMinute(now), MAX_RECHECK_MS);
      timer = window.setTimeout(evaluate, Math.max(250, Math.min(delay, MAX_TIMEOUT_MS)));
    };

    evaluate();
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [config]);

  return state;
}
